/**
 * The "3 replies ›" pill under a message that has a thread.
 *
 * Both message skins draw it, so it lives here rather than twice: the classic
 * `ChannelMessageItem` and the bubble `ConversationMessageItem` differ only in how much
 * room they put above it, which is what `className` carries.
 */

import { cn } from "cn";
import { isEmpty } from "lodash-es";
import { AvatarGroup } from "@/components/ui/avatar/avatar-group";
import { BwChat2Line, BwChevronRightCondensed } from "@/components/ui/icons";
import { Typography } from "@/components/ui/typography";
import type { ChannelMessage } from "../_api/channel-endpoints";

type ChannelThreadSummaryProps = {
  message: ChannelMessage;
  onOpen: () => void;
  className?: string;
};

function ChannelThreadSummary({
  message,
  onOpen,
  className,
}: ChannelThreadSummaryProps) {
  return (
    <button
      type="button"
      data-test="reply-count"
      onClick={onOpen}
      className={cn(
        /* sorted */
        "flex flex-row items-center gap-1",
        "w-fit",
        "py-0.5 pr-1 pl-0.5",
        "bg-bg-surface-elevate-low border border-border-dark-low rounded-full",
        "cursor-pointer",
        "transition-colors duration-100",
        "hover:bg-bg-dark-min",
        className,
      )}
    >
      {isEmpty(message.replies) ? (
        <BwChat2Line size={16} className="mx-1 shrink-0 text-text-dark-high" />
      ) : (
        <AvatarGroup
          data={message.replies.map((replier) => ({
            src: replier.avatarUrl || "",
            name: replier.firstName || "",
          }))}
          size="sm"
          // The group's own ring is `ring-background`, a token that does not exist, so
          // the avatars separate in `currentColor` unless the ring is named here.
          className="shrink-0 [&_[data-slot=avatar]]:ring-ring-light-max"
        />
      )}
      <div className="flex items-center gap-0.5">
        <Typography
          size="sm"
          // A button is shrink-to-fit even inside a flex row, so the label wraps onto two
          // lines long before the pill runs out of room.
          className="whitespace-nowrap text-text-dark-high"
        >
          {message.replyCount} {message.replyCount === 1 ? "reply" : "replies"}
        </Typography>
        <BwChevronRightCondensed className="size-4 shrink-0 text-text-dark-high" />
      </div>
    </button>
  );
}

export { ChannelThreadSummary, type ChannelThreadSummaryProps };
